import React, { useState, useEffect } from 'react';
import { fixPriceNumbers } from '@/utils/planFeatures';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PricingCard from '../components/PricingCard';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import T from '@/components/StyledTranslation';
import { BackButton } from '@/components/BackButton';
import { trackSubscribeClick, trackPageView } from '@/utils/analytics';
import SEO from '@/components/SEO';
import { ProductSchema, PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import { sortPlans, monthlyBase, monthsLabel } from '@/utils/planPricing';

import { API } from '@/config';
import { getPrefetched, getResolvedData } from '@/utils/dataPrefetcher';


const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const defaultPlans = [
    { id: 'free', name: 'Free', price: 0, duration_months: 0, isActive: true, features: [] },
    { id: 'monthly', name: '1 månad', price: 249, duration: 1, durationType: 'months', order: 1, isActive: true, features: [] },
    { id: '3months', name: '3 månader', price: 499, duration: 3, durationType: 'months', order: 2, isPopular: true, badge: 'Mest populär', isActive: true, features: [] },
    { id: '6months', name: '6 månader', price: 699, duration: 6, durationType: 'months', order: 3, badge: 'Bästa värde', isActive: true, features: [] }
  ];
  const prefetchedPlans = getResolvedData('plans');
  const initialPlans = prefetchedPlans 
    ? sortPlans(prefetchedPlans.filter(plan => plan.isActive !== false))
    : defaultPlans;
  const [plans, setPlans] = useState(initialPlans);
  const [loading, setLoading] = useState(false);
  // TEMP: Visual-only toggle to preview how the B-körkort / Taxikort split
  // would look on the pricing page. No backend wiring — Taxi shows mock plans
  // with the same prices for now. Remove once the real feature is built.
  const [licenseTab, setLicenseTab] = useState(() => {
    // Deep-link support: /priser?license=taxi opens the Taxi tab directly so
    // links from the SEO landing page and marketing emails land correctly.
    try {
      const p = new URLSearchParams(window.location.search).get('license');
      return p === 'taxi' ? 'taxi' : 'b_korkort';
    } catch {
      return 'b_korkort';
    }
  });
  const isRTL = i18n.language === 'ar';

  const pricingFeatures = t('pricing.features', { returnObjects: true });

  useEffect(() => {
    fetchPlans();
  }, []);

  // التحقق من وجود خطة معلقة بعد تسجيل الدخول والتوجيه للدفع تلقائياً
  useEffect(() => {
    const processPendingPlan = async () => {
      if (isAuthenticated && token) {
        const pendingPlanId = localStorage.getItem('pendingPlanId');
        const pendingPlanPrice = localStorage.getItem('pendingPlanPrice');
        
        if (pendingPlanId && pendingPlanPrice) {
          // مسح البيانات المعلقة فوراً لمنع التكرار
          localStorage.removeItem('pendingPlanId');
          localStorage.removeItem('pendingPlanPrice');
          
          const price = parseFloat(pendingPlanPrice);
          
          // إذا كانت الخطة مجانية - توجيه للاختبارات
          if (price <= 0) {
            toast.success(
              i18n.language === 'ar' 
                ? 'مرحباً! يمكنك البدء بأسئلة مجانية الآن'
                : i18n.language === 'sv'
                ? 'Välkommen! Du kan börja med gratis frågor nu'
                : 'Welcome! You can start with free questions now'
            );
            navigate('/teoriprov');
            return;
          }
          
          // خطة مدفوعة - إنشاء جلسة دفع
          setLoadingPlan(pendingPlanId);
          // Pull the license type saved alongside the plan (defaults to
          // b_korkort for legacy pending checkouts) so the correct
          // subscription is activated on payment success.
          const pendingLic = (localStorage.getItem('pendingPlanLicense') || 'b_korkort').trim();
          localStorage.removeItem('pendingPlanLicense');
          try {
            const response = await axios.post(
              `${API}/checkout/session?plan_id=${pendingPlanId}&licenseType=${encodeURIComponent(pendingLic)}`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.url) {
              window.location.href = response.data.url;
            }
          } catch (error) {
            console.error('Auto-checkout error:', error);
            toast.error(
              i18n.language === 'ar' 
                ? 'خطأ في إنشاء جلسة الدفع. اختر الخطة مرة أخرى.'
                : 'Error creating checkout. Please select the plan again.'
            );
          } finally {
            setLoadingPlan(null);
          }
        }
      }
    };
    
    processPendingPlan();
  }, [isAuthenticated, token, navigate, i18n.language]);

  const fetchPlans = async () => {
    try {
      const prefetched = await getPrefetched('plans');
      const data = prefetched || (await axios.get(`${API}/subscription-plans/public`)).data;
      // Filter active plans and sort by price
      let activePlans = sortPlans(data.filter(plan => plan.isActive !== false));
      
      // If user is trial and has no questions remaining, hide free plans
      if (user?.subscription_status === 'trial' && (user?.questions_remaining <= 0 || user?.questions_answered >= user?.questions_limit)) {
        activePlans = activePlans.filter(plan => plan.price > 0);
      }
      
      setPlans(activePlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSubscribe = async (plan) => {
    trackSubscribeClick(plan.name, plan.price);
    if (!isAuthenticated) {
      // حفظ الخطة المختارة في localStorage للتوجيه بعد التسجيل
      localStorage.setItem('pendingPlanId', plan.id);
      localStorage.setItem('pendingPlanPrice', plan.price.toString());
      // Persist which license (B or Taxi) the guest picked BEFORE registering
      // so the auto-checkout after signup activates the correct subscription.
      localStorage.setItem('pendingPlanLicense', licenseTab);
      navigate('/registrera');
      return;
    }

    // Duplicate-purchase guards must respect which SUBSCRIPTION we're buying.
    // Buying Taxi while already having B is perfectly valid (and vice-versa).
    const isBuyingTaxi = licenseTab === 'taxi';
    const activeThisLic = isBuyingTaxi
      ? (user?.taxi_subscription_status === 'active')
      : (user?.subscription_status === 'active');
    const activePlanIdThisLic = isBuyingTaxi
      ? user?.taxi_subscription_plan_id
      : user?.subscription_plan_id;

    // Already on this exact plan for this license
    if (activeThisLic && activePlanIdThisLic === plan.id) {
      toast.error(
        i18n.language === 'ar'
          ? 'أنت مشترك بالفعل في هذه الخطة!'
          : i18n.language === 'sv'
          ? 'Du har redan denna plan!'
          : 'You are already subscribed to this plan!'
      );
      return;
    }

    // التحقق إذا كان المستخدم لديه اشتراك فعال
    // Same-license upgrade guard: only block a "downgrade" INSIDE the same
    // license. Buying Taxi while you have an active B (or vice-versa) must
    // always be allowed — they are separate products.
    if (activeThisLic && plan.price > 0) {
      const currentPlanPrice = plans.find(p => p.id === activePlanIdThisLic)?.price || 0;
      if (plan.price <= currentPlanPrice) {
        toast.error(
          i18n.language === 'ar'
            ? 'لديك اشتراك فعال. يمكنك الترقية لخطة أعلى أو إلغاء الاشتراك الحالي أولاً.'
            : i18n.language === 'sv'
            ? 'Du har en aktiv prenumeration. Du kan uppgradera till en högre plan eller avbryta din nuvarande prenumeration först.'
            : 'You have an active subscription. You can upgrade to a higher plan or cancel your current subscription first.'
        );
        return;
      }
    }

    // إذا كانت الخطة مجانية - توجيه للاختبارات مباشرة
    if (plan.price <= 0) {
      toast.success(
        i18n.language === 'ar' 
          ? 'مرحباً! يمكنك البدء بأسئلة مجاني الآن'
          : i18n.language === 'sv'
          ? 'Välkommen! Du kan börja med gratis frågor nu'
          : 'Welcome! You can start with free questions now'
      );
      navigate('/teoriprov');
      return;
    }

    // الخطة مدفوعة - توجيه لنظام الدفع
    setLoadingPlan(plan.id);
    try {
      const response = await axios.post(
        `${API}/checkout/session?plan_id=${plan.id}&licenseType=${licenseTab}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(
        error.response?.data?.detail || 
        (i18n.language === 'ar' 
          ? 'خطأ في إنشاء جلسة الدفع'
          : i18n.language === 'sv'
          ? 'Fel vid skapande av betalningssession'
          : 'Error creating checkout session')
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-slate-950" data-testid="pricing-page">
      <SEO page="pricing" />
      <ProductSchema />
      <PageBreadcrumbs page="pricing" language={i18n.language} />
      <Navbar />
      
      {/* Back Button */}
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <BackButton to="/" className="mb-4" />
      </div>
      <main className="flex-1 pt-2 sm:pt-4 pb-0">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pb-0">
          {/* Trial Expired Message */}
          {user?.subscription_status === 'trial' && (user?.questions_remaining <= 0 || user?.questions_answered >= user?.questions_limit) && (
            <div className="mx-4 sm:mx-0 mb-6 p-4 sm:p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-2 border-amber-200 dark:border-amber-800/30">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h3 className="font-bold text-amber-800 dark:text-amber-400 text-lg">
                    {i18n.language === 'ar' 
                      ? 'انتهت الفترة التجريبية المجانية!'
                      : i18n.language === 'sv'
                      ? 'Din gratis provperiod har löpt ut!'
                      : 'Your free trial has ended!'}
                  </h3>
                  <p className="text-amber-700 dark:text-amber-400/80 text-sm">
                    {i18n.language === 'ar' 
                      ? `لقد استخدمت ${user?.questions_limit || 30} سؤال مجاني. اختر إحدى الخطط أدناه للمتابعة.`
                      : i18n.language === 'sv'
                      ? `Du har använt ${user?.questions_limit || 30} gratis frågor. Välj en plan nedan för att fortsätta.`
                      : `You've used ${user?.questions_limit || 30} free questions. Choose a plan below to continue.`}
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* Header Section */}
          <div className="text-center mb-6 sm:mb-6 px-4 sm:px-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:py-2 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-semibold mb-2 sm:mb-2">
              <span>💰 {t('ui.prices')}</span>
            </div>
            <h1 className="text-2xl sm:text-5xl lg:text-6xl font-black text-slate-900 dark:text-slate-100 mb-2 sm:mb-2 leading-tight">
              <T k="pricing.title" />
            </h1>
            <p className="text-sm sm:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed"><T k="pricing.subtitle" /></p>
          </div>

          {/* Paid 1-hour trial — shown ABOVE the plans so first-time visitors
              see the low-friction 29 SEK entry point before the full plans. */}
          <div className="max-w-2xl mx-auto px-4 sm:px-0 mb-6">
          </div>

          {/* TEMP: License-type toggle (visual preview only) */}
          <div className="flex justify-center mb-6 sm:mb-8 px-4">
            <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-2xl p-1.5 shadow-inner">
              <button
                type="button"
                onClick={() => setLicenseTab('b_korkort')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-bold transition-all ${
                  licenseTab === 'b_korkort'
                    ? 'bg-white dark:bg-slate-950 text-emerald-600 shadow-md'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="pricing-tab-b"
              >
                🚗 B-körkort
              </button>
              <button
                type="button"
                onClick={() => setLicenseTab('taxi')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-bold transition-all ${
                  licenseTab === 'taxi'
                    ? 'bg-white dark:bg-slate-950 text-amber-600 shadow-md'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="pricing-tab-taxi"
              >
                🚕 Taxikort
              </button>
            </div>
          </div>
          {licenseTab === 'taxi' ? (
            <div className="text-center mb-4 -mt-2 space-y-1.5">
              <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                ✨ {i18n.language === 'ar' ? 'اشتراك مستقل — يفتح فقط بطاقات Taxi' : 'Separat prenumeration — låser upp endast Taxi-prov'}
              </span>
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px] sm:text-xs font-semibold">
                  🇸🇪 {i18n.language === 'ar' ? 'محتوى Taxi متوفر بالسويدية فقط'
                        : i18n.language === 'sv' ? 'Taxi-innehåll finns endast på svenska'
                        : 'Taxi content is available in Swedish only'}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center mb-4 -mt-2">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                ✨ {i18n.language === 'ar' ? 'اشتراك مستقل — يفتح فقط بطاقات B-körkort' : 'Separat prenumeration — låser upp endast B-körkort-prov'}
              </span>
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-6 max-w-7xl mx-auto mb-0 px-0 sm:px-0 auto-rows-fr">
            {plans.map((plan, index) => {
              // Get features based on language with fallback to English (all 9 languages)
              const lang = i18n.language;
              // Pick B-körkort vs Taxikort feature bullets based on the active
              // tab so the marketing copy matches what the user actually buys.
              const isTaxi = licenseTab === 'taxi';
              // Hard-coded fallback bullets so the Taxi tab still shows a
              // proper feature list even if the DB row is missing
              // `taxiFeaturesSv/Ar/En` (older prod DBs won't have them yet).
              const TAXI_FALLBACK = {
                sv: ['34 Taxi-övningsprov', '480+ taxifrågor', 'Säkerhet & beteende + Lagstiftning', 'AI-körlärare dygnet runt', 'Full tillgång'],
                ar: ['34 اختبار تاكسي', '480+ سؤال تاكسي', 'السلامة والسلوك + التشريعات', 'مدرب ذكي على مدار الساعة', 'وصول كامل'],
                en: ['34 Taxi practice tests', '480+ taxi questions', 'Safety & Behavior + Legislation', 'AI instructor 24/7', 'Full access'],
              };
              const featuresMap = isTaxi ? {
                ar: plan.taxiFeaturesAr,
                sv: plan.taxiFeaturesSv,
                en: plan.taxiFeaturesEn,
                tr: plan.taxiFeaturesTr,
                fa: plan.taxiFeaturesFa,
                so: plan.taxiFeaturesSo,
                pl: plan.taxiFeaturesPl,
                es: plan.taxiFeaturesEs,
                de: plan.taxiFeaturesDe,
              } : {
                ar: plan.featuresAr,
                sv: plan.featuresSv,
                en: plan.featuresEn,
                tr: plan.featuresTr,
                fa: plan.featuresFa,
                so: plan.featuresSo,
                pl: plan.featuresPl,
                es: plan.featuresEs,
                de: plan.featuresDe,
              };
              const rawFeatures = (
                featuresMap[lang]
                || (isTaxi ? plan.taxiFeaturesEn : plan.featuresEn)
                || (isTaxi ? plan.taxiFeatures : plan.features)
                || (isTaxi ? (TAXI_FALLBACK[lang] || TAXI_FALLBACK.en) : null)
              );
              const features = fixPriceNumbers(rawFeatures, plan, plans);
              
              // Get plan name based on language with fallback (all 9 languages)
              const nameMap = {
                ar: plan.nameAr,
                sv: plan.nameSv,
                en: plan.nameEn,
                tr: plan.nameTr,
                fa: plan.nameFa,
                so: plan.nameSo,
                pl: plan.namePl,
                es: plan.nameEs,
                de: plan.nameDe
              };
              const planName = nameMap[lang] || plan.nameEn || plan.name;
              
              // Get plan description based on language with fallback (all 9 languages)
              const descMap = {
                ar: plan.descriptionAr,
                sv: plan.descriptionSv,
                en: plan.descriptionEn,
                tr: plan.descriptionTr,
                fa: plan.descriptionFa,
                so: plan.descriptionSo,
                pl: plan.descriptionPl,
                es: plan.descriptionEs,
                de: plan.descriptionDe
              };
              const planDescription = descMap[lang] || plan.descriptionEn || plan.description;
              
              // Duration text with all 9 languages
              let durationText = '';
              if (plan.durationType === 'days') {
                const dayTexts = {
                  ar: `${plan.duration} يوم`,
                  sv: `${plan.duration} dagar`,
                  tr: `${plan.duration} gün`,
                  fa: `${plan.duration} روز`,
                  so: `${plan.duration} maalmood`,
                  pl: `${plan.duration} dni`,
                  es: `${plan.duration} días`,
                  de: `${plan.duration} Tage`,
                  en: `${plan.duration} days`
                };
                durationText = dayTexts[i18n.language] || dayTexts.en;
              } else if (plan.durationType === 'year' || plan.period === 'year') {
                const yearTexts = {
                  ar: `/سنة`,
                  sv: `/år`,
                  tr: `/yıl`,
                  fa: `/سال`,
                  so: `/sannad`,
                  pl: `/rok`,
                  es: `/año`,
                  de: `/Jahr`,
                  en: `/year`
                };
                durationText = yearTexts[i18n.language] || yearTexts.en;
              } else if (plan.durationType === 'months') {
                if (plan.duration === 1) {
                  const monthTexts = {
                    ar: `/شهر`,
                    sv: `/månad`,
                    tr: `/ay`,
                    fa: `/ماه`,
                    so: `/bishii`,
                    pl: `/miesiąc`,
                    es: `/mes`,
                    de: `/Monat`,
                    en: `/month`
                  };
                  durationText = monthTexts[i18n.language] || monthTexts.en;
                } else if (plan.duration === 6) {
                  const sixMonthTexts = {
                    ar: `/6 أشهر`,
                    sv: `/6 månader`,
                    tr: `/6 ay`,
                    fa: `/۶ ماه`,
                    so: `/6 bilood`,
                    pl: `/6 miesięcy`,
                    es: `/6 meses`,
                    de: `/6 Monate`,
                    en: `/6 months`
                  };
                  durationText = sixMonthTexts[i18n.language] || sixMonthTexts.en;
                } else {
                  durationText = `/${monthsLabel(plan.duration, i18n.language)}`;
                }
              }
              
              // Calculate display name for 6 months plan
              let displayPlanName = planName;
              if (plan.duration === 6 && plan.durationType === 'months') {
                const sixMonthNames = {
                  ar: '6 أشهر',
                  sv: '6 månader',
                  tr: '6 Ay',
                  fa: '۶ ماه',
                  so: '6 Bilood',
                  pl: '6 miesięcy',
                  es: '6 Meses',
                  de: '6 Monate',
                  en: '6 Months'
                };
                displayPlanName = sixMonthNames[i18n.language] || sixMonthNames.en;
              }

              // Check if this is the user's current plan
              // Helper function to get name string from plan.name (could be string or object)
              const getPlanNameString = (name) => {
                if (typeof name === 'string') return name;
                if (typeof name === 'object' && name !== null) return name.en || name.sv || '';
                return '';
              };
              const planNameStr = getPlanNameString(plan.name);
              const isFreePlan = plan.price === 0 || planNameStr.toLowerCase().includes('free') || planNameStr.toLowerCase().includes('gratis');
              const isCurrentPlan = (
                // للمستخدمين المجانيين/التجريبيين
                (isFreePlan && (user?.subscription_status === 'trial' || user?.subscription_plan === 'free' || user?.subscription_plan === 'trial')) ||
                // للمستخدمين المشتركين
                (user?.subscription_status === 'active' && (
                  user?.subscription_plan_id === plan.id || 
                  user?.subscription_plan?.toLowerCase() === planNameStr.toLowerCase() ||
                  user?.subscription_plan?.toLowerCase() === plan.nameSv?.toLowerCase() ||
                  user?.subscription_plan?.toLowerCase() === plan.nameAr?.toLowerCase() ||
                  (user?.subscription_plan === 'lifetime' && planNameStr.toLowerCase().includes('lifetime')) ||
                  (user?.subscription_plan === 'monthly_premium' && planNameStr.toLowerCase().includes('monthly')) ||
                  (user?.subscription_plan === 'six_months_premium' && (plan.duration === 6 || planNameStr.toLowerCase().includes('six')))
                ))
              );

              return (
                <PricingCard
                  key={plan.id}
                  plan={plan}
                  index={index}
                  totalPlans={plans.length}
                  features={features}
                  planName={displayPlanName}
                  planDescription={planDescription}
                  durationText={durationText}
                  onSubscribe={handleSubscribe}
                  loading={loadingPlan}
                  userSubscriptionStatus={user?.subscription_status}
                  isCurrentPlan={isCurrentPlan}
                  base={monthlyBase(plans)}
                  i18n={i18n}
                  t={t}
                />
              );
            })}
          </div>
        </div>
      </main>

      <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="pricing" /></div>
      <Footer />
    </div>
  );
};

export default PricingPage;
